import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { getRenewalTasks, RenewalTask } from '../src/Utils/renewalTaskApi';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, User, Building2, Calendar, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { ROUTES } from '../src/routes/config';
import { TaskDetailsPanel } from './TaskDetailsPanel';
import { Sheet, SheetContent } from './ui/sheet';

interface ClientGroup {
  clientId: string;
  clientName: string;
  tasks: RenewalTask[];
  total: number;
  completed: number;
  pending: number;
  overdue: number;
  critical: number;
}

export const StaffTasksView: React.FC = () => {
  const { staffId } = useParams<{ staffId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [staffName, setStaffName] = useState<string>('');
  const [clientGroups, setClientGroups] = useState<ClientGroup[]>([]);
  const [selectedTask, setSelectedTask] = useState<RenewalTask | null>(null);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!token || !staffId) return;

    const fetchStaffTasks = async () => {
      setLoading(true);
      try {
        // Fetch all tasks using pagination (max 100 per request)
        let allTasks: RenewalTask[] = [];
        let currentPage = 1;
        let hasMore = true;
        const limit = 100; // Backend max limit

        while (hasMore) {
          const response = await getRenewalTasks(
            { ownerUserId: staffId, page: currentPage, limit },
            token
          );

          if (response.success) {
            const tasks = response.data.tasks;
            allTasks = [...allTasks, ...tasks];
            
            // Check if there are more pages
            hasMore = response.data.pagination.hasNext;
            currentPage += 1;
          } else {
            throw new Error('Failed to load staff tasks');
          }
        }

        // Get staff name from first task
        if (allTasks.length > 0 && allTasks[0].owner) {
          const owner = allTasks[0].owner;
          const name = owner.firstName && owner.lastName
            ? `${owner.firstName} ${owner.lastName}`
            : owner.email || 'Unknown Staff';
          setStaffName(name);
        }

        // Group tasks by client
        const clientMap = new Map<string, ClientGroup>();

        allTasks.forEach((task) => {
          const key = task.clientId || 'unknown';
          if (!clientMap.has(key)) {
            clientMap.set(key, {
              clientId: task.clientId,
              clientName: task.clientName || 'Unknown Client',
              tasks: [],
              total: 0,
              completed: 0,
              pending: 0,
              overdue: 0,
              critical: 0,
            });
          }

          const group = clientMap.get(key)!;
          group.tasks.push(task);
          group.total += 1;

          if (task.status === 'CLOSED' || task.status === 'RESOLVED') {
            group.completed += 1;
          } else {
            group.pending += 1;
          }

          if (task.bucket === 'OVERDUE' || (task.dueDate && new Date(task.dueDate) < new Date())) {
            group.overdue += 1;
          }

          if (task.priority === 'P0') {
            group.critical += 1;
          }
        });

        // Sort clients by total tasks (descending)
        const sortedGroups = Array.from(clientMap.values()).sort(
          (a, b) => b.total - a.total
        );

        setClientGroups(sortedGroups);
      } catch (error: any) {
        console.error('Error fetching staff tasks:', error);
        toast.error(error.message || 'Failed to load staff tasks');
      } finally {
        setLoading(false);
      }
    };

    fetchStaffTasks();
  }, [token, staffId]);

  const getPriorityBadge = (priority: RenewalTask['priority']) => {
    const variants: Record<string, { className: string; label: string }> = {
      P0: { className: 'bg-red-100 text-red-800 border-red-300', label: 'P0' },
      P1: { className: 'bg-orange-100 text-orange-800 border-orange-300', label: 'P1' },
      P2: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'P2' },
      P3: { className: 'bg-blue-100 text-blue-800 border-blue-300', label: 'P3' },
      P4: { className: 'bg-gray-100 text-gray-800 border-gray-300', label: 'P4' },
    };

    const config = variants[priority] || variants.P4;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const getStatusBadge = (status: RenewalTask['status']) => {
    const variants: Record<string, { className: string }> = {
      CLOSED: { className: 'bg-green-100 text-green-800 border-green-300' },
      RESOLVED: { className: 'bg-green-100 text-green-800 border-green-300' },
      IN_PROGRESS: { className: 'bg-blue-100 text-blue-800 border-blue-300' },
      WAITING: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      OPEN: { className: 'bg-gray-100 text-gray-800 border-gray-300' },
    };

    const config = variants[status] || variants.OPEN;
    return <Badge variant="outline" className={config.className}>{status}</Badge>;
  };

  const toggleClientExpansion = (clientId: string) => {
    setExpandedClients((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  };

  const isClientExpanded = (clientId: string) => expandedClients.has(clientId);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.supervisorDashboard)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <User className="w-6 h-6" />
              {staffName || 'Staff Tasks'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Tasks grouped by client
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-600">Loading tasks...</span>
          </CardContent>
        </Card>
      ) : clientGroups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <User className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-sm text-gray-600">No tasks found for this staff member</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Clients & Tasks</CardTitle>
            <CardDescription>
              Click on a client row to expand and view tasks
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-center">Total Tasks</TableHead>
                    <TableHead className="text-center">Completed</TableHead>
                    <TableHead className="text-center">Pending</TableHead>
                    <TableHead className="text-center">Overdue</TableHead>
                    <TableHead className="text-center">Critical</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientGroups.map((group) => {
                    const isExpanded = isClientExpanded(group.clientId);
                    return (
                      <React.Fragment key={group.clientId}>
                        {/* Client Row */}
                        <TableRow
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => toggleClientExpansion(group.clientId)}
                        >
                          <TableCell>
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-blue-600" />
                              <span>{group.clientName}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center font-semibold">
                            {group.total}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="default" className="bg-green-500">
                              {group.completed}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="border-amber-400 text-amber-700">
                              {group.pending}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {group.overdue > 0 ? (
                              <Badge variant="destructive">{group.overdue}</Badge>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {group.critical > 0 ? (
                              <Badge className="bg-red-100 text-red-800 border-red-300">
                                {group.critical}
                              </Badge>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(ROUTES.clientDetailsView(group.clientId));
                              }}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                        {/* Expanded Tasks Rows */}
                        {isExpanded && (
                          <>
                            {/* Tasks Sub-header */}
                            <TableRow className="bg-gray-50">
                              <TableCell></TableCell>
                              <TableCell className="font-semibold text-sm text-gray-700">Task Name</TableCell>
                              <TableCell className="text-center font-semibold text-sm text-gray-700">Start Date</TableCell>
                              <TableCell className="text-center font-semibold text-sm text-gray-700">End Date</TableCell>
                              <TableCell className="text-center font-semibold text-sm text-gray-700">Status</TableCell>
                              <TableCell className="text-center font-semibold text-sm text-gray-700">Priority</TableCell>
                              <TableCell className="text-center font-semibold text-sm text-gray-700">Due Date</TableCell>
                              <TableCell className="text-center font-semibold text-sm text-gray-700">Bucket</TableCell>
                            </TableRow>
                            {/* Task Rows */}
                            {group.tasks.map((task) => (
                              <TableRow
                                key={task.id}
                                className="cursor-pointer hover:bg-gray-50 bg-gray-25"
                                onClick={() => setSelectedTask(task)}
                              >
                                <TableCell></TableCell>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    {task.bucket === 'OVERDUE' && (
                                      <AlertTriangle className="w-4 h-4 text-red-500" />
                                    )}
                                    <span>{task.itemName}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  {task.startDate ? (
                                    <div className="flex items-center justify-center gap-1">
                                      <Calendar className="w-3 h-3 text-gray-400" />
                                      <span className="text-sm">
                                        {format(new Date(task.startDate), 'MMM d, yyyy')}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {task.endDate ? (
                                    <div className="flex items-center justify-center gap-1">
                                      <Calendar className="w-3 h-3 text-gray-400" />
                                      <span className="text-sm">
                                        {format(new Date(task.endDate), 'MMM d, yyyy')}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {getStatusBadge(task.status)}
                                </TableCell>
                                <TableCell className="text-center">
                                  {getPriorityBadge(task.priority)}
                                </TableCell>
                                <TableCell className="text-center">
                                  {task.dueDate ? (
                                    <div className="flex items-center justify-center gap-1">
                                      <Calendar className="w-3 h-3 text-gray-400" />
                                      <span className="text-sm">
                                        {format(new Date(task.dueDate), 'MMM d, yyyy')}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {task.bucket === 'OVERDUE' ? (
                                    <Badge variant="destructive">Overdue</Badge>
                                  ) : (
                                    <Badge variant="outline">{task.bucket}</Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Sheet open={!!selectedTask} onOpenChange={(open) => !open && setSelectedTask(null)}>
        <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto p-0">
          {selectedTask && (
            <TaskDetailsPanel
              task={selectedTask}
              onUpdate={() => setSelectedTask(null)}
              onClose={() => setSelectedTask(null)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

